<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Account" avatarImage="/glass/img/admin-overview-account.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>{{account.companyName}}</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="State Code">
              {{account.stateCode}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Country Code">
              {{account.countryCode}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="ACN">{{account.companyACN}}</OverviewBoxListItem>
            <OverviewBoxListItem title="ABN">{{account.companyABN}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Currency">{{account.currencyCode}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Timezone">{{account.timezoneCode}}</OverviewBoxListItem>

            <OverviewBoxListItem title="Activated">
              {{formatBoolean(account.isAccountActivated)}}
            </OverviewBoxListItem>            
            <OverviewBoxListItem title="Created On">
              {{formatDate(account.createdAt)}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">
              {{formatDate(account.updatedAt)}}
            </OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tools>
            
          </template>

          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-summary">
              <i class="far fa-id-card-alt"></i> Account Summary
            </Tab>
            <Tab tabid="tab-users">
              <i class="far fa-user-circle"></i> Users
            </Tab>
            <Tab tabid="tab-billing">
              <i class="far fa-money-check-alt"></i> Billing
            </Tab>
            <Tab tabid="tab-xero">
              <i class="far fa-box-usd"></i> Xero
            </Tab>
            <Tab tabid="tab-myob">
              <i class="far fa-box-usd"></i> MYOB
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-summary">
              <div class="row" v-if="account.id">
                <div class="col-lg-12">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="accessProxyLoginForAccount(account.id)" icon="fa-sign-in-alt">Proxy Login As Owner</DropdownMenuItem>
                  </ActionDropMenu>

                  <ContentTitle>{{account.companyName}}</ContentTitle>
                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Info</th>
                        <th>Value</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                        <td><strong>Todays Order Count</strong></td>
                        <td>{{account.analytics.orderSummary.orderTotalCount || 0}}</td>
                      </tr>
                      <tr>
                        <td><strong>Todays Order Value</strong></td>
                        <td>{{account.analytics.orderSummary.orderTotalValue || 0}}</td>
                      </tr>
                      <tr>
                        <td><strong>Todays Order Panel Count</strong></td>
                        <td>{{account.analytics.orderSummary.panelTotalCount || 0}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>

                  <FormHorizontalRule />
                  <div v-if="!account.isAccountActivated">
                    <ContentTitle>Pending Activation</ContentTitle>
                    <ContentSubTitle>Invitation link is only active while the subscription is still active</ContentSubTitle>
                    <FormRow label="Invitation Link" v-if="account.activationInviteCode">
                      <FormStaticLabel>{{accountInvitationLink}} <ClipboardCopyButton class="ml-2" :content="accountInvitationLink" /></FormStaticLabel>
                    </FormRow>
                  </div>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-users">
              <div class="row"  v-if="account.id">
                <div class="col-lg-12">
                  <ContentTitle>Users</ContentTitle>

                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Active</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="user of account.users" :key="user.id">
                        <td>
                          <strong>{{user.name}}</strong> &nbsp;
                          <LabelBadge v-if="user.isOwner" labelClass="label-success">Account Owner</LabelBadge>
                        </td>
                        <td>{{user.email}}</td>
                        <td>{{formatBoolean(user.isActive)}}</td>
                        <td>{{user.createdAt | formatDateTime}}</td>
                        <td>
                          <ActionDropMenu :tableAction="true" :forceDropdown="true">
                            <DropdownMenuItem @click="accessProxyLoginForAccount(account.id, user)" icon="fa-sign-in-alt">Proxy Login As User</DropdownMenuItem>
                          </ActionDropMenu>
                        </td>
                      </tr>
                    </template>
                  </StaticDataTable>
                  
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-billing">
              <div class="row"  v-if="account.id">
                <div class="col-lg-12">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="createAccountSubscription" icon="fa-plus-circle">Create Subscription</DropdownMenuItem>
                    <DropdownMenuItem @click="createAccountPurchasedProduct" icon="fa-plus-circle">Add Product</DropdownMenuItem>
                  </ActionDropMenu>

                  <ContentTitle>Quotas</ContentTitle>
                  
                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Info</th>
                        <th>Value</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                        <td><strong>Users</strong></td>
                        <td>{{account.billingSummary.userActiveCount}} / {{account.billingSummary.userLimit}}</td>
                      </tr>
                      <tr>
                        <td><strong>File Storage</strong></td>
                        <td>{{formatDecimal(account.billingSummary.totalFileStorageSize, 2, true)}} / {{formatDecimal(account.billingSummary.fileStorageLimit, 2, true)}} GB ({{account.billingSummary.totalFileStorageCount}} Files)</td>
                      </tr>
                      <tr>
                        <td><strong>Total Used Support Time</strong></td>
                        <td>{{formatDuration(account.billingSummary.totalSupportUsedMinutes * 60, { hideSeconds: true })}} Hours</td>
                      </tr>
                      <tr>
                        <td><strong>Remaining Support Time</strong></td>
                        <td>{{formatDuration(remainingSupportTime * 60, { hideSeconds: true })}} Hours</td>
                      </tr>
                      <tr>
                        <td><strong>Total Used SMS Credits</strong></td>
                        <td>{{account.billingSummary.totalSMSUsedCredits}} Credits</td>
                      </tr>
                      <tr>
                        <td><strong>Remaining SMS Credits</strong></td>
                        <td>{{remainingSMSCredits}} Credits</td>
                      </tr>
                      
                    </template>
                  </StaticDataTable>
                  
                </div>
              </div>

              <FormHorizontalRule />

              <div class="row mt-4" v-if="account.id">
                <div class="col-lg-12">
                  <ContentTitle>Subscriptions</ContentTitle>

                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th>Sub Id</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Auto Renewal</th>
                        <th>Trial Active</th>
                        <th>Monthly Fee</th>
                        <th>Expiry</th>
                        <th>Action</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="subItem in billingSubscriptionItems" :key="subItem.id">
                        <td class="font-weight-bold">
                          #{{subItem.subscriptionId}}
                        </td>
                        <td class="font-weight-bold">
                          {{subItem.name}}
                        </td>
                        <td>
                          {{subItem.description}}
                        </td>
                        <td>
                          {{subItem.quantity}}
                        </td>
                        <td>
                          <div v-if="subItem.isAutoRenewEnabled">
                            <LabelBadge labelClass="label-status-active">ACTIVE</LabelBadge><br />
                          </div>
                          <div v-if="!subItem.isAutoRenewEnabled">
                            <LabelBadge labelClass="label-status-pending">STOPPED</LabelBadge><br />
                          </div>
                        </td>
                        <td>
                          {{formatBoolean(subItem.isTrialModeActive)}}
                        </td>
                        <td>
                          {{formatCurrency(subItem.monthlyFee)}}
                        </td>
                        <td>
                          {{formatDate(subItem.expiryDate)}}
                        </td>
                        <td>
                          <ActionDropMenu :tableAction="true" :forceDropdown="true">
                            <DropdownMenuItem @click="editSubscription(subItem)" icon="fa-pencil">Edit Subscription</DropdownMenuItem>
                          </ActionDropMenu>
                        </td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="billingSubscriptionItems.length == 0">No subscriptions found</template>
                  </StaticDataTable>
                </div>
              </div>


            <FormHorizontalRule />

              <div class="row mt-4" v-if="account.id">
                <div class="col-lg-12">
                  <ContentTitle>Purchased Products</ContentTitle>

                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Purchased</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="item in billingPurchasedProducts" :key="item.id">
                        <td class="font-weight-bold">
                          {{item.name}}
                        </td>
                        <td>
                          {{item.description}}
                        </td>
                        <td>
                          {{item.quantity}}
                        </td>
                        <td>
                          {{formatDateTime(item.createdAt)}}
                        </td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="billingPurchasedProducts.length == 0">No products found</template>
                  </StaticDataTable>

                  

                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-xero">
              <div class="row no-gutter" v-if="account.xeroData">
                <div class="col-6">
                  <ContentTitle>Xero Config</ContentTitle>
                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Info</th>
                        <th>Value</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                        <td><strong>Installed</strong></td>
                        <td>{{formatBoolean(account.xeroData.isInstalled)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Rate Limiter Next Sync Time</strong></td>
                        <td>{{formatDateTime(account.xeroData.rateLimiterSyncTime)}}</td>
                      </tr>
                      <tr v-if="account.xeroData.curToken">
                        <td><strong>Token Expiry</strong></td>
                        <td>{{formatDateTime(xeroTokenExpiry)}}</td>
                      </tr>
                      <tr v-if="account.xeroData.curToken">
                        <td><strong>Token Scope</strong></td>
                        <td>{{account.xeroData.curToken.scope}}</td>
                      </tr>
                      <tr>
                        <td><strong>Tenant ID</strong></td>
                        <td>{{account.xeroData.tenantId}}</td>
                      </tr>
                      <tr v-if="account.xeroData.organisation">
                        <td><strong>Organisation</strong></td>
                        <td>{{account.xeroData.organisation.name}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>                  
                </div>
                <div class="col-6" v-if="account.xeroData.options">
                  <ContentTitle>Xero Options</ContentTitle>
                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Option</th>
                        <th>Read</th>
                        <th>Write</th>
                        <th>Start Date</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                        <td><strong>Contacts</strong></td>
                        <td>-</td>
                        <td>{{formatBoolean(account.xeroData.options.contactWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.xeroData.options.contactSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Invoices</strong></td>
                        <td>-</td>
                        <td>{{formatBoolean(account.xeroData.options.invoiceWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.xeroData.options.invoiceSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Credit Notes</strong></td>
                        <td>-</td>
                        <td>{{formatBoolean(account.xeroData.options.creditNoteWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.xeroData.options.creditNoteSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Payments</strong></td>
                        <td>{{formatBoolean(account.xeroData.options.invoicePaymentReadSyncEnabled)}}</td>
                        <td>{{formatBoolean(account.xeroData.options.invoicePaymentWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.xeroData.options.invoicePaymentSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Overpayments</strong></td>
                        <td>{{formatBoolean(account.xeroData.options.overpaymentReadSyncEnabled)}}</td>
                        <td>{{formatBoolean(account.xeroData.options.overpaymentWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.xeroData.options.overpaymentSyncStartDate)}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>                  
                </div>
              </div>

              <FormHorizontalRule />

              <div class="row no-gutter" v-if="account.xeroData && account.xeroData.syncErrorList">
                <div class="col-12">
                  <ContentTitle>Sync Errors</ContentTitle>

                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th width="10%">Type</th>
                        <th width="15%">Name</th>
                        <th width="50%">Message</th>
                        <th width="15%">Date</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="syncError of account.xeroData.syncErrorList" :key="`${syncError.linkType}-${syncError.linkId}`">
                        <template v-if="syncError.linkType == LinkTypes.contact">
                          <td>Contact</td>
                          <td><LinkContact :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.invoice">
                          <td>Invoice</td>
                          <td><LinkInvoice :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.overPayment">
                          <td>Over Payment</td>
                          <td><LinkOverPayment :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.paymentReceived">
                          <td>Payment Received</td>
                          <td><LinkPaymentReceived :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.creditNote">
                          <td>Credit Note</td>
                          <td><LinkCreditNote :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <td>{{syncError.message}}</td>
                        <td>{{formatDateTime(syncError.syncErrorDate)}}</td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="account.xeroData.syncErrorList.length == 0">
                      No sync errors in the system
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-myob">
              <div class="row no-gutter" v-if="account.myobData">
                <div class="col-6">
                  <ContentTitle>MYOB Config</ContentTitle>
                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Info</th>
                        <th>Value</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                        <td><strong>Installed</strong></td>
                        <td>{{formatBoolean(account.myobData.isInstalled)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Rate Limiter Next Sync Time</strong></td>
                        <td>{{formatDateTime(account.myobData.rateLimiterSyncTime)}}</td>
                      </tr>
                      <tr v-if="account.myobData.curToken">
                        <td><strong>Token Expiry</strong></td>
                        <td>{{formatDateTime(myobTokenExpiry)}}</td>
                      </tr>
                      <tr v-if="account.myobData.curToken">
                        <td><strong>Token Scope</strong></td>
                        <td>{{account.myobData.curToken.scope}}</td>
                      </tr>
                      <tr>
                        <td><strong>Company File ID</strong></td>
                        <td>{{account.myobData.companyFileId}}</td>
                      </tr>
                      <tr>
                        <td><strong>Company File Name</strong></td>
                        <td>{{account.myobData.companyFileName}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>                  
                </div>
                <div class="col-6" v-if="account.myobData.options">
                  <ContentTitle>MYOB Options</ContentTitle>
                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Option</th>
                        <th>Read</th>
                        <th>Write</th>
                        <th>Start Date</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                        <td><strong>Contacts</strong></td>
                        <td>-</td>
                        <td>{{formatBoolean(account.myobData.options.contactWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.myobData.options.contactSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Invoices</strong></td>
                        <td>-</td>
                        <td>{{formatBoolean(account.myobData.options.invoiceWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.myobData.options.invoiceSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Credit Notes</strong></td>
                        <td>-</td>
                        <td>{{formatBoolean(account.myobData.options.creditNoteWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.myobData.options.creditNoteSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Payments</strong></td>
                        <td>{{formatBoolean(account.myobData.options.invoicePaymentReadSyncEnabled)}}</td>
                        <td>{{formatBoolean(account.myobData.options.invoicePaymentWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.myobData.options.invoicePaymentSyncStartDate)}}</td>
                      </tr>
                      <tr>
                        <td><strong>Overpayments</strong></td>
                        <td>-</td>
                        <td>{{formatBoolean(account.myobData.options.overpaymentWriteSyncEnabled)}}</td>
                        <td>{{formatDate(account.myobData.options.overpaymentSyncStartDate)}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>                  
                </div>
              </div>

              <FormHorizontalRule />

              <div class="row no-gutter" v-if="account.myobData && account.myobData.syncErrorList">
                <div class="col-12">
                  <ContentTitle>Sync Errors</ContentTitle>

                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th width="10%">Type</th>
                        <th width="15%">Name</th>
                        <th width="50%">Message</th>
                        <th width="15%">Date</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="syncError of account.myobData.syncErrorList" :key="`${syncError.linkType}-${syncError.linkId}`">
                        <template v-if="syncError.linkType == LinkTypes.contact">
                          <td>Contact</td>
                          <td><LinkContact :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.invoice">
                          <td>Invoice</td>
                          <td><LinkInvoice :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.overPayment">
                          <td>Over Payment</td>
                          <td><LinkOverPayment :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.paymentReceived">
                          <td>Payment Received</td>
                          <td><LinkPaymentReceived :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <template v-if="syncError.linkType == LinkTypes.creditNote">
                          <td>Credit Note</td>
                          <td><LinkCreditNote :id="syncError.linkId" :name="syncError.name" /></td>
                        </template>
                        <td>{{syncError.message}}</td>
                        <td>{{formatDateTime(syncError.syncErrorDate)}}</td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="account.myobData.syncErrorList.length == 0">
                      No sync errors in the system
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import DeveloperEditSubDialog from '@/modules/developer/views/developerEditSubDialog'
import DeveloperPurchaseProductDialog from '@/modules/developer/views/developerPurchaseProductDialog'
import DeveloperProxyLogin from '@/modules/developer/views/developerProxyLogin'

import { LinkTypes } from '@/store/types'

export default {
  name: 'DeveloperAccount',
  data() {
    return {
      account: {},
      billingProductList: []
    }
  },
  computed: {
    remainingSupportTime() {
      var totalUsedMinutesTime = this.account.billingSummary.totalSupportUsedMinutes
      return Math.max((this.account.billingSummary.supportPurchasedMinutes - totalUsedMinutesTime), 0)
    },
    remainingSMSCredits() {
      var totalUsedSMSCredits = this.account.billingSummary.totalSMSUsedCredits
      return Math.max((this.account.billingSummary.availableSMSCredits - totalUsedSMSCredits), 0)
    },
    billingSubscriptionItems() {
      let subItems = []

      if(this.account.billingSummary.subscriptionList) {
        this.account.billingSummary.subscriptionList.forEach(sub => {
          if(sub.subscriptionItems) {
            sub.subscriptionItems.forEach(subItem => {
              subItems.push({
                id: subItem.id,
                subscriptionId: sub.id,
                isActive: subItem.isActive,
                isAutoRenewEnabled: sub.isAutoRenewEnabled,
                isTrialModeActive: sub.isTrialModeActive,
                monthlyFee: sub.monthlyFee,
                expiryDate: sub.expiryDate,
                quantity: subItem.quantity,

                isAddonProduct: subItem.isAddonProduct,
                name: subItem.name,
                description: subItem.description,
                availableUserCount: subItem.availableUserCount,
                availableStorageGigabytes: subItem.availableStorageGigabytes,
                availableSupportMinutes: subItem.availableSupportMinutes,
                featureList: subItem.featureList
              })
            })
          }
        })
      }
      return subItems
    },
    billingPurchasedProducts() {
      let purchasedProducts = []

      if(this.account.billingSummary.purchasedProductList) {
        this.account.billingSummary.purchasedProductList.forEach(productInfo => {
          purchasedProducts.push({
            id: productInfo.id,
            isActive: productInfo.isActive,
            quantity: productInfo.quantity,
            createdAt: productInfo.createdAt,

            isAddonProduct: productInfo.isAddonProduct,
            name: productInfo.name,
            description: productInfo.description,
            availableUserCount: productInfo.availableUserCount,
            availableStorageGigabytes: productInfo.availableStorageGigabytes,
            availableSupportMinutes: productInfo.availableSupportMinutes,
            featureList: productInfo.featureList
          })
        })
      }
      return purchasedProducts
    },
    xeroTokenExpiry() {
      if(this.account.xeroData && this.account.xeroData.curToken)
        return moment.unix(this.account.xeroData.curToken.expires_at)
      return 0
    },
    myobTokenExpiry() {
      if(this.account.myobData && this.account.myobData.curToken)
        return moment(this.account.myobData.curToken.expires_at)
      return 0
    },
    accountInvitationLink() {
      return `${process.env.VUE_APP_FRONTEND_URL}/account/invitation/${this.account.activationInviteCode}`
    }
  },
  created() {
    this.LinkTypes = LinkTypes
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.developerService.developerGetAccount(this.$route.params.id),
        this.$apiService.developerService.developerGetBillingProducts(),
      ]))
      .then(results => {
        this.account = results[0].data
        this.billingProductList = results[1].data
      })
    },
    accessProxyLoginForAccount(accountId, user = null) {
      this.displayModal(DeveloperProxyLogin, { accountId: accountId, user: user })
      .then((success) => {
      })
    },
    editSubscription(subItem) {
      var subscription = this.account.billingSummary.subscriptionList.find(sub => sub.id == subItem.subscriptionId)

      this.displayModal(DeveloperEditSubDialog, {
        isCreateMode: false,
        accountId: this.account.id,
        subscriptionId: subItem.subscriptionId
      }, subscription)
      .then((success) => {
        if(success) {
          this.requestData()
          this.successToast('Subscription Updated')
        }
      })
    },
    createAccountSubscription() {
      this.displayModal(DeveloperEditSubDialog, {
        isCreateMode: true,
        accountId: this.account.id,
      })
      .then((success) => {
        if(success) {
          this.requestData()
          this.successToast('Subscription Created')
        }
      })
    },
    createAccountPurchasedProduct() {
      this.displayModal(DeveloperPurchaseProductDialog, {
        accountId: this.account.id,
      })
      .then((success) => {
        if(success) {
          this.requestData()
          this.successToast('Purchased Product Created')
        }
      })
    }
  }


}
</script>